import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientApi } from '../../services/api';
import type { ClientFormData, Client } from '../../types';
import { validateDocument, formatCEP, fetchAddressByCEP } from '../../utils/documentValidation';
import SignatoryManager from '../../components/SignatoryManager';

interface ClientFormProps {
  mode: 'create' | 'edit';
  defaultClientType?: 'customer' | 'supplier' | 'both';
  returnTo?: string;
}

const ClientForm: React.FC<ClientFormProps> = ({ mode, defaultClientType = 'customer', returnTo }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';
  const backUrl = returnTo || '/clients';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    person_type: 'PJ',
    client_type: defaultClientType,
    cpf_cnpj: '',
    email: '',
    phone: '',
    whatsapp: '',
    website: '',
    address: '',
    address_number: '',
    address_complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    is_active: true,
    observations: '',
    signatory_name: '',
    signatory_email: '',
    signatory_phone: '',
    signatory_role: '',
    signatory_document: '',
    signatories: [],
  });

  // Load client data for editing
  useEffect(() => {
    if (isEdit && id) {
      loadClient(id);
    }
  }, [isEdit, id]);

  const loadClient = async (clientId: string) => {
    try {
      setLoading(true);
      const client = await clientApi.getClient(clientId);
      setFormData({
        name: client.name || '',
        person_type: client.person_type || 'PJ',
        client_type: client.client_type || 'customer',
        cpf_cnpj: client.cpf_cnpj || '',
        email: client.email || '',
        phone: client.phone || '',
        whatsapp: client.whatsapp || '',
        website: client.website || '',
        address: client.address || '',
        address_number: client.address_number || '',
        address_complement: client.address_complement || '',
        neighborhood: client.neighborhood || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
        is_active: client.is_active,
        observations: client.observations || '',
        signatory_name: client.signatory_name || '',
        signatory_email: client.signatory_email || '',
        signatory_phone: client.signatory_phone || '',
        signatory_role: client.signatory_role || '',
        signatory_document: client.signatory_document || '',
        signatories: client.signatories?.map(sig => ({
          name: sig.name,
          email: sig.email,
          phone: sig.phone,
          document: sig.document,
          role: sig.role,
          is_primary: sig.is_primary,
          is_active: sig.is_active,
          observations: sig.observations,
        })) || [],
      });
    } catch (error) {
      console.error('Error loading client:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    
    // Auto-add https:// if not present and value is not empty
    if (value && !value.match(/^https?:\/\//)) {
      value = `https://${value}`;
    }
    
    setFormData(prev => ({ ...prev, website: value }));
    
    // Clear error when field is modified
    if (errors.website) {
      setErrors(prev => ({ ...prev, website: '' }));
    }
  };

  const formatDocument = (value: string, type: 'PF' | 'PJ'): string => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    if (type === 'PF') {
      // CPF: 000.000.000-00
      if (cleaned.length <= 11) {
        return cleaned
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      }
    } else {
      // CNPJ: 00.000.000/0000-00
      if (cleaned.length <= 14) {
        return cleaned
          .replace(/(\d{2})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1/$2')
          .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
      }
    }
    
    return value;
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDocument(e.target.value, formData.person_type);
    setFormData(prev => ({ ...prev, cpf_cnpj: formatted }));
    
    if (errors.cpf_cnpj) {
      setErrors(prev => ({ ...prev, cpf_cnpj: '' }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const cleaned = value.replace(/\D/g, '');
    let formatted = cleaned;
    
    if (cleaned.length >= 11) {
      formatted = cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length >= 10) {
      formatted = cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length >= 6) {
      formatted = cleaned.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
    } else if (cleaned.length >= 2) {
      formatted = cleaned.replace(/(\d{2})(\d+)/, '($1) $2');
    }

    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, zip_code: formatted }));
    
    // Auto-fill address when CEP is complete
    const cleaned = formatted.replace(/\D/g, '');
    if (cleaned.length === 8) {
      fetchAddressByZipCode(formatted);
    }
  };

  const fetchAddressByZipCode = async (cep: string) => {
    try {
      setLoadingCEP(true);
      const addressData = await fetchAddressByCEP(cep);

      setFormData(prev => ({
        ...prev,
        ...addressData
      }));

      // Clear any CEP-related errors
      if (errors.zip_code) {
        setErrors(prev => ({ ...prev, zip_code: '' }));
      }
    } catch (error) {
      console.warn('CEP lookup failed:', error);
      // Don't show error to user, just log it
    } finally {
      setLoadingCEP(false);
    }
  };

  const handleBuscarCNPJ = async () => {
    if (!formData.cpf_cnpj) {
      alert('Por favor, digite um CNPJ');
      return;
    }

    if (formData.person_type !== 'PJ') {
      alert('Busca de CNPJ disponível apenas para Pessoa Jurídica');
      return;
    }

    try {
      setLoadingCNPJ(true);
      const resultado = await clientApi.consultarCNPJ(formData.cpf_cnpj);

      if (resultado.success) {
        // Preenche o formulário com os dados retornados
        setFormData(prev => ({
          ...prev,
          ...resultado.data
        }));

        alert('✅ Dados importados da Receita Federal com sucesso!');
      }
    } catch (error: any) {
      console.error('Erro ao consultar CNPJ:', error);
      const mensagem = error.response?.data?.error || 'Erro ao consultar CNPJ na Receita Federal';
      alert('❌ ' + mensagem);
    } finally {
      setLoadingCNPJ(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!formData.cpf_cnpj.trim()) {
      newErrors.cpf_cnpj = 'CPF/CNPJ é obrigatório';
    } else if (!validateDocument(formData.cpf_cnpj, formData.person_type)) {
      newErrors.cpf_cnpj = formData.person_type === 'PF' ? 'CPF inválido' : 'CNPJ inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await clientApi.updateClient(id, formData);
      } else {
        await clientApi.createClient(formData);
      }
      navigate(backUrl);
    } catch (error: any) {
      console.error('Error saving client:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndStartProposal = async () => {
    console.log('🚀 handleCreateAndStartProposal - Iniciando...');

    if (!validateForm()) {
      console.error('❌ Validação do formulário falhou');
      alert('Por favor, preencha todos os campos obrigatórios antes de continuar.');
      return;
    }

    console.log('✅ Validação passou, salvando cliente...');
    setSaving(true);

    try {
      console.log('📤 Enviando dados do cliente:', formData);
      const newClient = await clientApi.createClient(formData);
      console.log('✅ Cliente criado com sucesso:', newClient);

      // Redireciona para nova proposta com cliente pré-selecionado
      const targetUrl = `/proposals/new?clientId=${newClient.id}`;
      console.log('➡️ Navegando para:', targetUrl);
      navigate(targetUrl);
    } catch (error: any) {
      console.error('❌ Error saving client:', error);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);

      if (error.response?.data) {
        setErrors(error.response.data);

        // Mostrar detalhes do erro
        const errorDetails = JSON.stringify(error.response.data, null, 2);
        console.error('❌ Detalhes do erro:', errorDetails);
        alert('Erro ao salvar cliente:\n\n' + errorDetails);
      } else {
        alert('Erro ao salvar cliente: ' + (error.message || 'Erro desconhecido'));
      }
      setSaving(false);
    }
    // Não desativa saving aqui pois vai navegar para outra página
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-1">
        <h1 className="text-base font-bold text-secondary-900">
          {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
        </h1>
        <p className="text-xs text-secondary-600">
          {isEdit ? 'Atualize as informações do cliente' : 'Cadastre um novo cliente no sistema'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-1">
        {/* Basic Information */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Tipo de Pessoa *
              </label>
              <select
                name="person_type"
                value={formData.person_type}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="PJ">Pessoa Jurídica</option>
                <option value="PF">Pessoa Física</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Relacionamento *
              </label>
              <select
                name="client_type"
                value={formData.client_type || 'customer'}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="customer">Cliente</option>
                <option value="supplier">Fornecedor</option>
                <option value="both">Cliente e Fornecedor</option>
              </select>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                {formData.person_type === 'PF' ? 'CPF *' : 'CNPJ *'}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="cpf_cnpj"
                  value={formData.cpf_cnpj}
                  onChange={handleDocumentChange}
                  placeholder={formData.person_type === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                  className={`input-field flex-1 ${errors.cpf_cnpj ? 'border-red-300' : ''}`}
                  maxLength={formData.person_type === 'PF' ? 14 : 18}
                  required
                />
                {formData.person_type === 'PJ' && (
                  <button
                    type="button"
                    onClick={handleBuscarCNPJ}
                    disabled={loadingCNPJ || !formData.cpf_cnpj}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                      loadingCNPJ || !formData.cpf_cnpj
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {loadingCNPJ ? (
                      <span className="flex items-center gap-1">
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Buscando...
                      </span>
                    ) : (
                      '🔍 Buscar'
                    )}
                  </button>
                )}
              </div>
              {errors.cpf_cnpj && <p className="text-red-600 text-xs mt-0.5">{errors.cpf_cnpj}</p>}
            </div>

            <div className="md:col-span-6">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                {formData.person_type === 'PF' ? 'Nome Completo *' : 'Razão Social *'}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                required
              />
              {errors.name && <p className="text-red-600 text-xs mt-0.5">{errors.name}</p>}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações de Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field ${errors.email ? 'border-red-300' : ''}`}
                required
              />
              {errors.email && <p className="text-red-600 text-xs mt-0.5">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Telefone *
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                className={`input-field ${errors.phone ? 'border-red-300' : ''}`}
                required
              />
              {errors.phone && <p className="text-red-600 text-xs mt-0.5">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                WhatsApp
              </label>
              <input
                type="text"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Website
              </label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleWebsiteChange}
                placeholder="exemplo.com ou www.exemplo.com"
                className="input-field"
              />
              <p className="text-xs text-secondary-500 mt-1">https:// será adicionado automaticamente</p>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-1">
            {/* CEP primeiro para facilitar preenchimento automático */}
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                CEP
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleCEPChange}
                  placeholder="00000-000"
                  maxLength={9}
                  className="input-field"
                />
                {loadingCEP && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  </div>
                )}
              </div>
              <p className="text-xs text-secondary-500 mt-1">Digite CEP para preenchimento automático</p>
            </div>

            <div className="lg:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Endereço
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Número
              </label>
              <input
                type="text"
                name="address_number"
                value={formData.address_number}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Complemento
              </label>
              <input
                type="text"
                name="address_complement"
                value={formData.address_complement}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Bairro
              </label>
              <input
                type="text"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Cidade
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Estado
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={2}
                placeholder="SP"
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Signatory/Contact Information */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Dados do Signatário/Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Nome do Signatário/Contato
              </label>
              <input
                type="text"
                name="signatory_name"
                value={formData.signatory_name}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Email do Signatário/Contato
              </label>
              <input
                type="email"
                name="signatory_email"
                value={formData.signatory_email}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Telefone do Signatário/Contato
              </label>
              <input
                type="text"
                name="signatory_phone"
                value={formData.signatory_phone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                className="input-field"
                maxLength={15}
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Cargo do Signatário/Contato
              </label>
              <input
                type="text"
                name="signatory_role"
                value={formData.signatory_role}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Documento do Signatário/Contato
              </label>
              <input
                type="text"
                name="signatory_document"
                value={formData.signatory_document}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Multiple Signatories - NEW */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <SignatoryManager
            signatories={formData.signatories || []}
            onChange={(signatories) => setFormData(prev => ({ ...prev, signatories }))}
          />
        </div>

        {/* Additional Information */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações Adicionais</h3>
          <div className="space-y-1">
            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-xs text-secondary-900">
                Cliente ativo
              </label>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Observações
              </label>
              <textarea
                name="observations"
                value={formData.observations}
                onChange={handleChange}
                rows={2}
                className="input-field"
                placeholder="Observações adicionais sobre o cliente..."
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-1 mt-2">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="btn-secondary"
            disabled={saving}
          >
            Cancelar
          </button>

          {!isEdit && (
            <button
              type="button"
              onClick={handleCreateAndStartProposal}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </div>
              ) : (
                '✨ Criar Cliente e Iniciar Proposta'
              )}
            </button>
          )}

          <button
            type="submit"
            disabled={saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </div>
            ) : (
              isEdit ? 'Atualizar Cliente' : 'Criar Cliente'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;